import React, { useCallback } from 'react';
import { motion } from 'framer-motion';
import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import { AboutHero } from './components/AboutHero';
import { FeaturesGrid } from './components/FeaturesGrid';
import { StatsSection } from './components/StatsSection';
import { APP_VERSION, COMPANY_NAME, COPYRIGHT_YEAR } from './lib/about-data';
import { cn } from '../../shared/ui/kit/utils';
import { useToast } from '../../shared/hooks/useToast';

interface AboutPageProps {
    className?: string;
}

export const AboutPage: React.FC<AboutPageProps> = ({ className }) => {
    const { showToast } = useToast();
    const containerRef = React.useRef<HTMLDivElement>(null);

    useGSAP(() => {
        const ctx = gsap.context(() => {
            gsap.to('.parallax-bg', {
                yPercent: 50,
                ease: 'none',
                scrollTrigger: {
                trigger: containerRef.current,
                start: 'top top',
                end: 'bottom top',
                scrub: true,
                },
            });
        }, containerRef);

        return () => ctx.revert();
    }, { scope: containerRef });

    const handleFeatureClick = useCallback((featureId: string) => {
        const messages: Record<string, string> = {
            monitoring: 'Круглосуточный мониторинг активен',
            instant: 'Push-уведомления настроены',
            simple: 'Интуитивный интерфейс',
            mobile: 'PWA установлено',
            finance: 'Интеграция с 1С',
            secure: 'Безопасность на максимуме',
        };
        
        showToast(messages[featureId] || 'Функция доступна', 'success');
    }, [showToast]);

    return (
        <motion.div
            ref={containerRef}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className={cn(
                'min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-950',
                'relative overflow-hidden',
                className
            )}
        >
            <div className="parallax-bg absolute inset-0 pointer-events-none overflow-hidden">
                <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />
                <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-purple-500/5 rounded-full blur-3xl" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-radial from-blue-500/3 to-transparent" />
            </div>

            <div className="relative z-10 max-w-5xl mx-auto">
                <AboutHero />
                
                <FeaturesGrid onFeatureClick={handleFeatureClick} />
                
                <StatsSection />
                
                <motion.footer
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 }}
                    className="py-8 text-center border-t border-black/[0.06] dark:border-white/[0.06] mx-4"
                >
                    <div className="flex items-center justify-center gap-2 mb-3">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-sm font-bold">
                            S
                        </div>
                        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                            {COMPANY_NAME}
                        </span>
                    </div>
                    
                    <p className="text-xs text-gray-500 dark:text-gray-500 mb-2">
                        Версия {APP_VERSION} · {COPYRIGHT_YEAR}
                    </p>
                    
                    <div className="flex items-center justify-center gap-4 text-xs text-gray-400 dark:text-gray-600">
                        <button 
                            onClick={() => showToast('Политика конфиденциальности', 'info')}
                            className="hover:text-gray-600 dark:hover:text-gray-400 transition-colors"
                        >
                            Конфиденциальность
                        </button>
                        <span>·</span>
                        <button 
                            onClick={() => showToast('Условия использования', 'info')}
                            className="hover:text-gray-600 dark:hover:text-gray-400 transition-colors"
                        >
                            Условия использования
                        </button>
                        <span>·</span>
                        <button 
                            onClick={() => showToast('Поддержка: support@scanta.ru', 'info')}
                            className="hover:text-gray-600 dark:hover:text-gray-400 transition-colors"
                        >
                            Поддержка
                        </button>
                    </div>
                </motion.footer>
            </div>

            <div className="h-safe-area-inset-bottom md:hidden" />
        </motion.div>
    );
};